import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { TaxRateSchedule } from '~hooks/useTaxRateSchedule/models';
import useTaxRateSchedule from '~hooks/useTaxRateSchedule/useTaxRateSchedule';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import TaxRateScheduleForm, { TaxRateScheduleFormHandler } from './TaxRateScheduleForm';
import {
  TaxRateScheduleDto,
  TaxRateScheduleDtoAsyncError,
} from './taxRateScheduleSchema';

type TaxRateScheduleDialogState = {
  isOpen: boolean;
  isSubmitting: boolean;
  taxRateSchedule?: TaxRateSchedule;
};

export const TaxRateScheduleManagement = observer(() => {
  const { userStore, toasterStore } = useStores();

  const companyId = getEffectiveUserCompanyId(userStore);
  const {
    createTaxRateSchedule,
    deleteTaxRateSchedule,
    getTaxRateSchedules,
    updateTaxRateSchedule,
  } = useTaxRateSchedule();
  const { searchValue, setSearchQueryValue } = useDataGridSearch();

  const { goToPage, isFetching, pagination, rows, setRows, startOver } =
    useCursorPagination<TaxRateSchedule>({
      fetchPage: (params) => {
        if (!companyId) {
          return Promise.resolve({
            data: [],
            pagination: { limit: params.limit, page: 1 },
          });
        }

        return getTaxRateSchedules({ companyId, searchQuery: searchValue, ...params });
      },
      limit: DEFAULT_API_PAGE_SIZE,
      onFetchError: (error) => {
        console.error('Failed to load tax rate schedules:', error);
      },
    });

  const taxRateScheduleFormRef = useRef<TaxRateScheduleFormHandler>(null);
  const [formDialogState, setFormDialogState] = useState<TaxRateScheduleDialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [formDialogAsyncErrors, setFormDialogAsyncErrors] = useState<
    TaxRateScheduleDtoAsyncError[]
  >([]);

  const [deleteDialogState, setDeleteDialogState] = useState<TaxRateScheduleDialogState>({
    isOpen: false,
    isSubmitting: false,
  });

  useEffect(() => {
    void startOver();
  }, [companyId, searchValue, startOver]);

  const onChangeQuickFilter = (query: string) => {
    setSearchQueryValue(query);
  };

  const isMutating = formDialogState.isSubmitting || deleteDialogState.isSubmitting;

  const { canCreateRate, canDeleteRate, canEditRate } = userStore.getPermissions();

  const handleCreateClick = useCallback(() => {
    setFormDialogState({ isOpen: true, isSubmitting: false });
  }, []);

  const handleDeleteClick = useCallback((taxRateSchedule: TaxRateSchedule) => {
    setDeleteDialogState({
      isOpen: true,
      isSubmitting: false,
      taxRateSchedule,
    });
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    setFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleSubmitFormDialog = useCallback(async () => {
    const formData = await taxRateScheduleFormRef.current?.onSubmit();

    if (!formData) {
      return;
    }

    const existingId = formDialogState.taxRateSchedule?.id;

    try {
      setFormDialogAsyncErrors([]);
      setFormDialogState((state) => ({ ...state, isSubmitting: true }));

      if (existingId) {
        const body = TaxRateSchedule.deparseUpdate({
          name: formData.name,
          rate: formData.rate,
          externalAccountingId: formData.externalAccountingId,
          externalId: formData.externalId,
        });

        const updated = await updateTaxRateSchedule(
          formData.company.value,
          existingId,
          body,
        );
        setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
        toasterStore.push(
          alert(
            t('administration.tax_rate_schedule.updated', { name: updated.name }),
            AlertTypes.success,
          ),
        );
      } else {
        const body = TaxRateSchedule.deparseCreate({
          name: formData.name,
          rate: formData.rate,
          externalAccountingId: formData.externalAccountingId,
          externalId: formData.externalId,
        });

        const created = await createTaxRateSchedule(formData.company.value, body);
        setRows((prev) => [...prev, created]);
        toasterStore.push(
          alert(
            t('administration.tax_rate_schedule.created', { name: created.name }),
            AlertTypes.success,
          ),
        );
      }

      setFormDialogState({ isOpen: false, isSubmitting: false });
    } catch (error) {
      setFormDialogState((state) => ({ ...state, isSubmitting: false }));

      if (axios.isAxiosError(error)) {
        const errorsArray = (error.response?.data?.error?.errors ??
          []) as ModelError_Item[];

        const asyncErrors: TaxRateScheduleDtoAsyncError[] = [];
        const fieldMap: Record<string, keyof TaxRateScheduleDto> = {
          name: 'name',
        };

        if (error.response?.status === 422) {
          errorsArray.forEach((errorsArrayItem) => {
            const { field, message } = errorsArrayItem;
            const formField = fieldMap[field];

            if (formField) {
              asyncErrors.push({ field: formField, message });
            }
          });
        }

        if (asyncErrors.length) {
          setFormDialogAsyncErrors(asyncErrors);
          return;
        }

        if (!existingId && error.response?.status !== 422) {
          // Create errors outside 422 are handled in useTaxRateSchedule.createTaxRateSchedule.
          return;
        }

        if (existingId && error.response?.status !== 422) {
          // Update errors outside 422 are handled in useTaxRateSchedule.updateTaxRateSchedule.
          return;
        }

        connection.handleRequestError(
          error,
          existingId
            ? t('error_messages.tax_rate_schedules.failed_to_update')
            : t('error_messages.tax_rate_schedules.failed_to_create'),
        );
      } else if (!existingId) {
        // Non-axios create errors are handled in useTaxRateSchedule.createTaxRateSchedule.
      } else {
        // Non-axios update errors are handled in useTaxRateSchedule.updateTaxRateSchedule.
      }
    }
  }, [createTaxRateSchedule, updateTaxRateSchedule, formDialogState.taxRateSchedule?.id]);

  const handleDeleteConfirm = useCallback(async () => {
    const taxRateScheduleId = deleteDialogState.taxRateSchedule?.id;
    const taxRateScheduleCompanyId = deleteDialogState.taxRateSchedule?.company?.id;

    if (!taxRateScheduleId || !taxRateScheduleCompanyId) {
      return;
    }

    try {
      setDeleteDialogState((state) => ({ ...state, isSubmitting: true }));

      await deleteTaxRateSchedule(taxRateScheduleCompanyId, taxRateScheduleId);

      setRows((prev) => prev.filter((row) => row.id !== taxRateScheduleId));
      toasterStore.push(
        alert(t('administration.tax_rate_schedule.deleted'), AlertTypes.success),
      );
      setDeleteDialogState({ isOpen: false, isSubmitting: false });
    } catch {
      setDeleteDialogState((state) => ({ ...state, isSubmitting: false }));
    }
  }, [
    deleteTaxRateSchedule,
    deleteDialogState.taxRateSchedule?.id,
    deleteDialogState.taxRateSchedule?.company?.id,
  ]);

  const columns = useMemo<Array<DataTableColumn<TaxRateSchedule>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (taxRateSchedule) => taxRateSchedule.name,
      },
      {
        field: 'rate',
        headerName: t('form_fields.rate'),
        valueGetter: (taxRateSchedule) =>
          isNil(taxRateSchedule.rate) ? '' : `${parseFloat(taxRateSchedule.rate)}%`,
      },
      {
        field: 'externalAccountingId',
        headerName: t('form_fields.external_accounting_id'),
        valueGetter: (taxRateSchedule) => taxRateSchedule.externalAccountingId,
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (taxRateSchedule) => (
          <Tooltip
            title={`${t('common.last_update')}: ${dateFormat(taxRateSchedule.updatedAt)}`}
          >
            <span>{dateFormat(taxRateSchedule.createdAt)}</span>
          </Tooltip>
        ),
      },
      actionsColumn((taxRateSchedule) => {
        const options = canDeleteRate
          ? [
              {
                title: t('actions.delete'),
                icon: <Delete />,
                callBack: () => handleDeleteClick(taxRateSchedule),
              },
            ]
          : [];

        return (
          <>
            {canEditRate && (
              <IconButton
                aria-label={t('actions.edit')}
                size="small"
                onClick={() => {
                  setFormDialogState({
                    isOpen: true,
                    isSubmitting: false,
                    taxRateSchedule,
                  });
                }}
              >
                <Edit />
              </IconButton>
            )}
            {options.length > 0 && (
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            )}
          </>
        );
      }),
    ],
    [canDeleteRate, canEditRate, handleDeleteClick],
  );

  useEffect(() => {
    if (!formDialogState.isOpen) {
      setFormDialogAsyncErrors([]);
    }
  }, [formDialogState.isOpen]);

  return (
    <>
      <SettingsPageHeader
        title={t('administration.tax_rate_schedule.title')}
        description={t('settings.page_descriptions.tax_rate_schedules')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          disabled={!canCreateRate}
          onClick={handleCreateClick}
          startIcon={<Add />}
        >
          {t('administration.tax_rate_schedule.create')}
        </SmallButton>
      </Box>
      <Box data-test-id="tax-rate-schedule-data-grid">
        <DataTable
          id="tax-rate-schedule-data-grid"
          columns={columns}
          rows={rows}
          loading={isFetching || isMutating}
          onSearchChange={onChangeQuickFilter}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetching || isMutating}
              onNavigate={(link) => void goToPage(link)}
              pagination={pagination}
            />
          }
        />
      </Box>

      <Dialog
        isOpen={formDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseFormDialog}>
          {formDialogState.taxRateSchedule
            ? t('administration.tax_rate_schedule.update')
            : t('administration.tax_rate_schedule.create')}
        </DialogHeader>

        <DialogContent>
          <TaxRateScheduleForm
            ref={taxRateScheduleFormRef}
            asyncErrors={formDialogAsyncErrors}
            taxRateSchedule={formDialogState.taxRateSchedule}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(formDialogAsyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={formDialogState.isSubmitting}
            onClick={handleCloseFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            loading={formDialogState.isSubmitting}
            onClick={handleSubmitFormDialog}
          >
            {formDialogState.taxRateSchedule ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isBusy={deleteDialogState.isSubmitting}
        isOpen={deleteDialogState.isOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteConfirm}
        title={t('administration.tax_rate_schedule.delete')}
      >
        <Typography>
          {t('administration.tax_rate_schedule.delete_description')}
        </Typography>
      </ConfirmationDialog>
    </>
  );
});
